import { cssVar, extendTheme } from '@chakra-ui/react'

const $arrowBg = cssVar('popper-arrow-bg')

const theme = extendTheme({
  textStyles: {
    h3: {
      fontSize: '40',
      lineHeight: '46px',
      fontWeight: '500',
    },
    h4: {
      fontSize: '32',
      lineHeight: '38px',
      fontWeight: '500',
    },
    h5: {
      fontSize: '24',
      lineHeight: '29px',
      fontWeight: '500',
    },
    'b-xs': {
      fontSize: '12',
      lineHeight: '14px',
    },
    'b-sm': {
      fontSize: '14',
      lineHeight: '17px',
    },
    'b-md': {
      fontSize: '16',
      lineHeight: '19px',
    },
    'b-lg': {
      fontSize: '18px',
      lineHeight: '27px',
    },
    'b-xl': {
      fontSize: '20px',
      lineHeight: '27px',
    },
  },
  fonts: {
    body: 'SFProText-Regular, sans-serif',
    heading: 'SFProText-Bold, sans-serif',
  },
  colors: {
    primary: {
      10: '#E7E6F5',
      50: '#cfcdeb',
      100: '#afabde',
      200: '#8681ce',
      300: '#5e58be',
      400: '#362ead',
      500: '#0e049d',
      600: '#0c0383',
      700: '#090369',
      800: '#07024f',
      900: '#040136',
    },
    neutral: {
      10: '#F8F8F9',
      30: '#E2E8F0',
      50: '#F0F1F2',
      60: '#64748B',
      100: '#E0E2E4',
      200: '#C1C5C9',
      300: '#A2A8AE',
      400: '#838B93',
      500: '#646E78',
      600: '#505860',
      700: '#3C4248',
      800: '#252935',
      900: '#141618',
    },
    secondary: {
      50: '#cfcdeb',
      100: '#afabde',
      200: '#8681ce',
      300: '#5e58be',
      400: '#362ead',
      500: '#0e049d',
      600: '#0c0383',
      700: '#090369',
    },
    tertiary: {
      50: '#d2f5fd',
      100: '#b4effc',
      200: '#8ee7fa',
      300: '#68dff9',
      400: '#43d7f7',
      500: '#1dcff6',
      600: '#18accd',
      700: '#138aa4',
    },
    //border: 1.5px solid #1FC489
    progressColor: {
      100: '#f7fafc',
      500: '#362EAD',
      900: '#1a202c',
    },
    shade: {
      100: '#000000',
    },
  },
  styles: {
    global: {
      body: {
        bg: '#f8f8f9',
        overflowX: 'hidden',
      },
    },
  },
  components: {
    Avatar: {
      baseStyle: {
        group: { gap: '5px' },
        excessLabel: {
          bg: 'neutral.50',
          color: 'neutral.900',
        },
      },
    },
    Select: {
      variants: {
        default: {
          field: {
            height: '32px',
            borderRadius: '8px',
            transition: '0.2s ease-out',

            bg: 'white',
            boxShadow: '0px 2px 4px -2px #00000005',

            _focus: {
              borderWidth: '1px',
              borderColor: 'secondary.500',
              boxShadow: '0 0 0 2px #CFCDEB',
            },
            _disabled: {
              bg: 'neutral.100',
              border: 'none',
            },
            color: 'neutral.700',
            fontSize: '12',
            fontWeight: '400',

            borderWidth: '1px',
            borderColor: 'neutral.100',

            _hover: { bg: 'neutral.50' },
            _active: { bg: 'neutral.100' },
          },
          icon: {
            color: 'neutral.300',
          },
        },
      },
      defaultProps: {
        variant: 'default',
      },
    },
    Popover: {
      baseStyle: {
        popper: {
          zIndex: 9999,
        },
        content: {
          borderWidth: '1px',
          borderColor: 'neutral.100',
        },
      },
    },
    Input: {
      variants: {
        default: {
          field: {
            height: '32px',
            borderRadius: '8px',
            transition: '0.2s ease-out',

            bg: 'white',
            boxShadow: '0px 2px 4px -2px #00000005',

            _focus: {
              borderWidth: '1px',
              borderColor: 'secondary.500',
              boxShadow: '0 0 0 2px #CFCDEB',
            },
            _disabled: {
              bg: 'neutral.100',
              border: 'none',
            },
            _placeholder: { color: 'neutral.300' },
            color: 'neutral.700',
            fontSize: '12',
            fontWeight: '400',

            borderWidth: '1px',
            borderColor: 'neutral.100',
          },
        },
        auth: {
          field: {
            height: '48px',
            borderRadius: '8px',
            transition: '0.2s ease-out',

            bg: 'white',
            boxShadow: '0px 2px 4px -2px #00000005',

            _focus: {
              borderWidth: '1px',
              borderColor: 'secondary.500',
              boxShadow: '0 0 0 2px #CFCDEB',
            },
            _disabled: {
              bg: 'neutral.100',
              border: 'none',
            },

            _placeholder: { color: 'neutral.300' },
            color: 'neutral.700',
            fontSize: '16',
            fontWeight: '400',

            borderWidth: '1px',
            borderColor: 'neutral.100',
          },
        },
      },
      defaultProps: {
        variant: 'default',
      },
    },

    Textarea: {
      variants: {
        default: {
          padding: '8px 10px',
          borderRadius: '8px',
          transition: '0.2s ease-out',
          bg: 'white',
          _focus: {
            borderWidth: '1px',
            borderColor: 'secondary.500',
            boxShadow: '0 0 0 2px #CFCDEB',
          },
          _disabled: {
            bg: 'neutral.100',
            border: 'none',
          },
          _placeholder: { color: 'neutral.300' },
          color: 'neutral.700',
          fontSize: '12',
          fontWeight: '400',

          borderWidth: '1px',
          borderColor: 'neutral.100',
        },
      },
      defaultProps: {
        variant: 'default',
      },
    },
    Button: {
      variants: {
        default: {
          height: '32px',
          borderRadius: '8px',
          transition: '0.2s ease-out',

          bg: 'white',
          boxShadow: '0px 2px 4px -2px #00000005',

          color: 'secondary.500',
          fontSize: '12',
          fontWeight: '510',

          borderWidth: '1px',
          borderColor: 'neutral.100',

          _focus: {
            borderColor: 'secondary.500',
            outline: 'none',
            boxShadow: '0 0 0 2px #CFCDEB',
            _disabled: {},
          },
          _hover: {
            bg: 'neutral.10',
            _disabled: {
              bg: 'neutral.100',
              borderColor: 'neutral.200',
              color: 'neutral.300',
            },
          },
          _active: {
            bg: 'secondary.100',
            borderColor: 'secondary.200',
            _disabled: {
              bg: 'neutral.100',
              borderColor: 'neutral.200',
              color: 'neutral.300',
            },
          },
          _disabled: {
            bg: 'neutral.100',
            borderColor: 'neutral.200',
            color: 'neutral.300',
          },
        },
        neutral: {
          height: '32px',
          borderRadius: '8px',
          transition: '0.2s ease-out',

          bg: 'white',
          boxShadow: '0px 2px 4px -2px #00000005',

          color: 'neutral.900',
          fontSize: '12',
          fontWeight: '510',

          borderWidth: '1px',
          borderColor: 'neutral.100',

          _focus: {
            borderColor: 'secondary.500',
            outline: 'none',
            boxShadow: '0 0 0 2px #CFCDEB',
            _disabled: {},
          },
          _hover: {
            bg: 'neutral.10',
            _disabled: {
              bg: 'neutral.100',
              borderColor: 'neutral.200',
              color: 'neutral.300',
            },
          },
          _active: {
            bg: 'secondary.100',
            borderColor: 'secondary.200',
            _disabled: {
              bg: 'neutral.100',
              borderColor: 'neutral.200',
              color: 'neutral.300',
            },
          },
          _disabled: {
            bg: 'neutral.100',
            borderColor: 'neutral.200',
            color: 'neutral.300',
          },
        },
        rounded: {
          bg: 'white',
          color: 'black',
          borderRadius: '50%',
          _hover: {
            bg: 'neutral.50',
            _disabled: { bg: 'neutral.50' },
          },
          _active: {
            bg: 'neutral.100',
            _disabled: { bg: 'neutral.50' },
          },
          _disabled: {
            bg: 'white',
          },
          _focus: {
            borderColor: 'secondary.500',
            borderWidth: '1px',
            outline: 'none',
            boxShadow: '0 0 0 2px #CFCDEB',
            _disabled: {},
          },
        },
        heavy: {
          height: '32px',
          borderRadius: '8px',
          transition: '0.2s ease-out',

          bg: 'secondary.500',
          boxShadow: '0px 2px 4px -2px #00000005',

          color: 'white',
          fontSize: '12',
          fontWeight: '510',

          _focus: {
            borderColor: 'secondary.500',
            outline: 'none',
            boxShadow: '0 0 0 2px #CFCDEB',
            _disabled: {},
          },
          _hover: {
            color: 'secondary.50',
            bg: 'secondary.600',
            _disabled: {
              bg: 'neutral.100',
              color: 'neutral.300',
            },
          },
          _active: {
            bg: 'secondary.200',
            borderColor: 'secondary.700',
            _disabled: {
              bg: 'neutral.100',
              color: 'neutral.300',
            },
          },
          _disabled: {
            bg: 'neutral.100',
            color: 'neutral.300',
          },
          _loading: {
            bg: 'secondary.500',
            color: 'white',
            _hover: { bg: 'secondary.600' },
          },
        },
        link: {
          bg: 'transparent',
          color: 'secondary.500',
          fontSize: '14',
          fontWeight: '510',
          _hover: {
            bg: 'transparent',
            textDecoration: 'underline',
          },
          _active: {
            bg: 'transparent',
            textDecoration: 'underline',
          },
        },
        warning: {
          height: '32px',
          bg: '#E3502F',
          color: 'white',
          borderRadius: '8px',
          transition: '0.2s ease-out',
          boxShadow: '0px 2px 4px -2px #00000005',

          fontSize: '12',
          fontWeight: '510',

          _focus: {
            borderColor: '#E3502F',
            outline: 'none',
            boxShadow: '0 0 0 2px #FFB6A0', // Slightly lighter shade for focus outline
            _disabled: {},
          },
          _hover: {
            color: 'white',
            bg: '#D64529', // Slightly darker shade for hover
            _disabled: {
              bg: 'neutral.100',
              color: 'neutral.300',
            },
          },
          _active: {
            bg: '#B13C25', // Even darker shade for active state
            borderColor: '#8C3120',
            _disabled: {
              bg: 'neutral.100',
              color: 'neutral.300',
            },
          },
          _disabled: {
            bg: 'neutral.100',
            color: 'neutral.300',
          },
          _loading: {
            bg: '#E3502F',
            color: 'white',
            _hover: { bg: '#D64529' },
          },
        },
        ghost: {
          bg: 'transparent',
          color: 'secondary.500',
          borderRadius: '8px',
          transition: '0.2s ease-out',
          fontSize: '12',
          fontWeight: '510',

          borderWidth: '1px',
          borderColor: 'transparent',

          _focus: {
            borderColor: 'secondary.500',
            outline: 'none',
            boxShadow: '0 0 0 2px #CFCDEB',
            _disabled: {},
          },
          _hover: {
            bg: 'neutral.50',
            _disabled: {
              bg: 'neutral.100',
              borderColor: 'neutral.200',
              color: 'neutral.300',
            },
          },
          _active: {
            bg: 'neutral.100',
            borderColor: 'neutral.200',
            _disabled: {
              bg: 'neutral.100',
              borderColor: 'neutral.200',
              color: 'neutral.300',
            },
          },
          _disabled: {
            bg: 'transparent',
            borderColor: 'transparent',
            color: 'neutral.300',
          },
        },
      },
      defaultProps: {
        variant: 'default',
      },
    },
    Divider: {
      baseStyle: {
        borderColor: 'neutral.50',
      },
    },
    Tooltip: {
      baseStyle: {
        backgroundColor: 'black',
        color: 'white',
        [$arrowBg.variable]: 'black',
        borderRadius: '6px',
        p: '6px 8px',
        fontSize: '12',
        fontWeight: '510',
      },
    },
    Table: {
      variants: {
        light: {
          th: {
            p: '8px 2px',
            height: '18px',
            color: 'neutral.300',
            textStyle: 'b-xs',
            fontWeight: '510',
            borderBottomColor: 'neutral.50',
            borderBottomWidth: '1px',
          },
          td: {
            p: '8px 2px',
            height: '18px',
            color: 'neutral.900',
            textStyle: 'b-xs',
            fontWeight: '400',
            borderBottomColor: 'neutral.50',
            borderBottomWidth: '1px',
          },
        },
        listed: {
          table: {
            border: '1px solid neutral.50',
            marginTop: '16px',
            borderRadius: '8px',
            overflow: 'hidden',
          },
          thead: {
            th: {
              fontSize: '12px',
              lineHeight: '14px',
              borderWidth: '1px',
              borderColor: 'neutral.50',
              textTransform: 'none',
              padding: '8px',
              fontWeight: '510',
            },
            backgroundColor: 'neutral.10',
            height: '34px',
          },
          tbody: {
            td: {
              transition: '0.2s ease-out',
              fontSize: '14px',
              padding: '0px 8px',
              lineHeight: '17px',
              height: '40px',
              borderWidth: '1px',
              borderColor: 'neutral.50',
              fontWeight: '400',
            },
          },
        },
        darkListed: {
          table: {
            border: '1px solid neutral.800',
            borderRadius: '8px',
            overflow: 'hidden',
            borderColor: 'neutral.800',
            borderWidth: '1px',
          },
          thead: {
            th: {
              fontSize: '12px',
              lineHeight: '14px',
              borderWidth: '1px',
              borderColor: 'neutral.800',
              textTransform: 'none',
              padding: '8px',
              fontWeight: '510',
            },
            backgroundColor: 'neutral.10',
            height: '34px',
          },
          tbody: {
            borderColor: 'neutral.800',
            borderWidth: '1px',
            tr: {
              borderColor: 'neutral.800',
              borderWidth: '1px',
              td: {
                transition: '0.2s ease-out',
                fontSize: '14px',
                padding: '0px 8px',
                lineHeight: '17px',
                height: '40px',
                borderWidth: '1px',
                borderColor: 'neutral.800',
                borderRadius: '8px',
                fontWeight: '400',
              },
            },
          },
        },
      },
    },
    Modal: {
      variants: {
        default: {
          header: {
            fontSize: '24',
            lineHeight: '29px',
            fontWeight: '510',
            padding: '24px 0',
          },
          dialog: {
            padding: '40px 48px',
            borderRadius: '24px',
          },
          closeButton: {
            borderRadius: '50%',
            _focus: {
              borderColor: 'secondary.500',
              borderWidth: '1px',
              outline: 'none',
              boxShadow: '0 0 0 2px #CFCDEB',
              _disabled: {},
            },
          },
          body: {
            padding: '24px 0',
          },
          footer: {
            padding: '24px 0 0 0',
            button: {
              mr: 'auto',
              backgroundColor: 'secondary.500',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '10px',
              _focus: {
                borderColor: 'secondary.500',
                outline: 'none',
                boxShadow: '0 0 0 2px #CFCDEB',
                _disabled: {},
              },
              _hover: {
                color: 'secondary.50',
                bg: 'secondary.600',
                _disabled: {
                  bg: 'neutral.100',
                  color: 'neutral.300',
                },
              },
              _active: {
                bg: 'secondary.200',
                borderColor: 'secondary.700',
                _disabled: {
                  bg: 'neutral.100',
                  color: 'neutral.300',
                },
              },
              _disabled: {
                bg: 'neutral.100',
                color: 'neutral.300',
              },
              _loading: {
                bg: 'secondary.500',
                color: 'white',
                _hover: { bg: 'secondary.600' },
              },
            },
          },
        },
      },
      defaultProps: {
        variant: 'default',
        isCentered: true,
      },
    },
    Checkbox: {
      baseStyle: {
        control: {
          transition: '0.2s ease-out',
          borderRadius: '5px',
          bg: 'neutral.10',
          borderColor: 'neutral.30',
          _checked: {
            bg: 'secondary.500',
            borderColor: 'secondary.500',
            _hover: {
              bg: 'secondary.600',
              borderColor: 'secondary.500',
            },
          },
          _indeterminate: {
            bg: 'secondary.500',
            borderColor: 'secondary.500',
            _hover: {
              bg: 'secondary.600',
              borderColor: 'secondary.500',
            },
          },
          _hover: {
            bg: 'neutral.30',
          },
          _focus: {
            borderColor: 'secondary.500',
            borderWidth: '1px',
            outline: 'none',
            boxShadow: '0 0 0 2px #CFCDEB',
            _disabled: {},
          },
        },
      },
    },
    Menu: {
      baseStyle: {
        list: { borderRadius: '8px' },
        item: {
          transition: '0.2s ease-out',
          h: '32px',
          color: 'black',
          fontSize: '14',
          textStyle: 'b-sm',
        },
      },
    },
    Drawer: {
      variants: {
        nonBlocking: {
          dialogContainer: {
            pointerEvents: 'none',
          },
          dialog: {
            pointerEvents: 'auto',
          },
          overlay: {
            pointerEvents: 'auto',
          },
        },
      },
    },
    Switch: {
      baseStyle: {
        track: {
          _focus: {
            boxShadow: 'none',
            border: 'none',
            outline: 'none',
          },
        },
      },
    },
  },
})

export default theme
